<template>
  <div class="emh-ui-button-group-tab-view">
    <ui-button-group
      v-if="shouldShowButtonGroup"
      class="emh-ui-button-group-tab-view__tabs"
      :disabled="disabled"
      :options="buttonGroupOptions"
      :group-name="groupName"
      data-test-id="emh-ui-button-group-tab-view__tabs"
      @change-option="changeTab"
    />
    <div class="emh-ui-button-group-tab-view__content" data-test-id="emh-ui-button-group-tab-view__content">
      <slot :name="displayedSelectedTab"></slot>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'UIButtonGroupTabView' };
</script>

<script lang="ts" setup>
import { computed, PropType, ref, toRefs } from 'vue';

import UiButtonGroup from '../ButtonGroup.vue';
import { ButtonGroupTabView } from './button-group-tab-view.model';
import { ButtonGroupOption } from '../button-group.model';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  tabs: {
    type: Array as PropType<ButtonGroupTabView[]>,
    required: true,
  },
  groupName: {
    type: String,
    default: 'button-group-tab-view',
  },
  selectedTab: {
    type: String,
    default: undefined,
  },
  initialTabValue: {
    type: String,
    default: undefined,
  },
  manageTabSelection: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change-tab']);

const { tabs, selectedTab: selectedTabProp, manageTabSelection, initialTabValue, disabled } = toRefs(props);

const shouldShowButtonGroup = computed(() => tabs.value.length > 1);

const selectedTabState = ref(initialTabValue?.value ?? tabs.value[0]?.value);

const displayedSelectedTab = computed(() =>
  manageTabSelection.value ? selectedTabState.value : selectedTabProp?.value,
);

const buttonGroupOptions = computed(() =>
  tabs?.value.map(
    ({ label, value }): ButtonGroupOption => ({
      label,
      value,
      key: value,
      selected: value === displayedSelectedTab?.value,
      disabled: disabled.value,
    }),
  ),
);

const changeTab = ({ label, value }: ButtonGroupOption) => {
  if (manageTabSelection.value) {
    selectedTabState.value = value;

    return;
  }

  emit('change-tab', { label, value } as ButtonGroupTabView);
};
</script>

<style lang="scss">
.emh-ui-button-group-tab-view {
  &__tabs {
    @include wb-spacing('margin-bottom', 's');

    width: 100%;
  }
}
</style>
